import type { PublicMenu } from "./domain";

export type PublicMenuSelectionDraft = {
  guestNumber: number;
  itemId: string;
  modifiers: Array<{ groupId: string; optionIds: string[] }>;
};

export function validatePublicMenuSelections(
  menu: PublicMenu | undefined,
  selections: PublicMenuSelectionDraft[],
  guestCount: number,
) {
  if (!menu) return selections.length === 0;
  const items = new Map(
    menu.sections.flatMap((section) =>
      section.items.map((item) => [item.id, item] as const),
    ),
  );
  const sectionByItem = new Map(
    menu.sections.flatMap((section) =>
      section.items.map((item) => [item.id, section] as const),
    ),
  );
  const sharedChoices = new Map<string, string>();
  const seen = new Set<string>();
  for (const selection of selections) {
    if (
      !Number.isInteger(selection.guestNumber) ||
      selection.guestNumber < 1 ||
      selection.guestNumber > guestCount
    )
      return false;
    const selectionKey = `${selection.guestNumber}:${selection.itemId}`;
    if (seen.has(selectionKey)) return false;
    seen.add(selectionKey);
    const item = items.get(selection.itemId);
    if (!item || item.orderable === false) return false;
    const selectedByGroup = new Map(
      selection.modifiers.map((group) => [group.groupId, group.optionIds]),
    );
    if (selectedByGroup.size !== selection.modifiers.length) return false;
    const knownGroupIds = new Set(
      (item.modifierGroups ?? []).map((group) => group.id),
    );
    if ([...selectedByGroup.keys()].some((id) => !knownGroupIds.has(id)))
      return false;
    for (const group of item.modifierGroups ?? []) {
      const optionIds = selectedByGroup.get(group.id) ?? [];
      if (
        new Set(optionIds).size !== optionIds.length ||
        optionIds.length < group.minSelections ||
        optionIds.length > group.maxSelections
      )
        return false;
      if (
        optionIds.some(
          (optionId) =>
            !group.options.some(
              (option) =>
                option.id === optionId && option.orderable !== false,
            ),
        )
      )
        return false;
      if (group.sameOptionForAllGuests) {
        const key = `${item.id}:${group.id}`;
        const canonical = [...optionIds].sort().join(":");
        if (sharedChoices.has(key) && sharedChoices.get(key) !== canonical)
          return false;
        sharedChoices.set(key, canonical);
      }
    }
  }
  for (const section of menu.sections) {
    const sectionSelections = selections.filter(
      (selection) => sectionByItem.get(selection.itemId)?.id === section.id,
    );
    const limitType = section.limitType ?? "unlimited";
    const minimum =
      limitType === "custom"
        ? Math.max(0, section.minimumSelections ?? 0)
        : limitType === "one_per_person" && section.required
          ? guestCount
          : section.required
            ? 1
            : 0;
    const maximum =
      limitType === "one_per_order"
        ? 1
        : limitType === "one_per_person"
          ? guestCount
          : limitType === "custom"
            ? Math.max(0, section.maximumSelections ?? 0)
            : Number.POSITIVE_INFINITY;
    if (
      sectionSelections.length < minimum ||
      sectionSelections.length > maximum
    )
      return false;
    if (limitType === "one_per_person") {
      const countByGuest = new Map<number, number>();
      for (const selection of sectionSelections)
        countByGuest.set(
          selection.guestNumber,
          (countByGuest.get(selection.guestNumber) ?? 0) + 1,
        );
      if ([...countByGuest.values()].some((count) => count > 1)) return false;
    }
    if (
      section.sameItemForAllGuests &&
      new Set(sectionSelections.map((selection) => selection.itemId)).size > 1
    )
      return false;
  }
  return true;
}

export const describePublicSectionRule = (
  section: PublicMenu["sections"][number],
  guestCount: number,
) => {
  const limitType = section.limitType ?? "unlimited";
  const limit =
    limitType === "one_per_order"
      ? "Choose up to 1 per reservation"
      : limitType === "one_per_person"
        ? `Choose up to ${guestCount} per reservation, one per guest`
        : limitType === "custom"
          ? `Choose ${section.minimumSelections ?? 0}–${section.maximumSelections ?? 0} per reservation`
          : "Choose any number";
  return [
    limit,
    section.required ? "required" : "optional",
    section.sameItemForAllGuests ? "same item for every guest" : null,
  ]
    .filter(Boolean)
    .join(" · ");
};
